// Render saved Markdown as inert React text. Source HTML, images, and links
// never execute or fetch anything; the exact original remains inspectable.
function Inline({ text }) {
  return text
    .split(/(`[^`\n]+`|\*\*[^*\n]+\*\*)/g)
    .map((part, i) =>
      part.startsWith("`") && part.endsWith("`") ? (
        <code key={i}>{part.slice(1, -1)}</code>
      ) : part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        part
      ),
    );
}

export default function SourceContent({ content, kind }) {
  const blocks = [];
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (/^```/.test(line)) {
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
      i++;
      blocks.push(
        <pre key={i}>
          <code>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      const Tag = heading[1].length < 3 ? "h3" : "h4";
      blocks.push(
        <Tag key={i}>
          <Inline text={heading[2]} />
        </Tag>,
      );
      i++;
      continue;
    }
    const list = /^\s*(?:[-*+] |\d+\. )/.test(line);
    if (list) {
      const ordered = /^\s*\d+\. /.test(line);
      const pattern = ordered ? /^\s*\d+\. / : /^\s*[-*+] /;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(
          <li key={i}>
            <Inline text={lines[i++].replace(pattern, "")} />
          </li>,
        );
      }
      const Tag = ordered ? "ol" : "ul";
      blocks.push(<Tag key={i}>{items}</Tag>);
      continue;
    }
    const paragraph = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(?:#{1,6}\s|```|\s*[-*+] |\s*\d+\. )/.test(lines[i])
    )
      paragraph.push(lines[i++]);
    const text = paragraph.join("\n");
    // Memory is intentionally concise. Separate its saved clauses for reading,
    // preserving every word and delimiter; do not generate new conclusions.
    const clauses =
      (kind === "memory" || kind === "preference") && !text.includes("\n")
        ? text.match(/[^;]+(?:;\s*|$)/g)
        : null;
    blocks.push(
      clauses?.length > 2 ? (
        <ul key={i} className="solar-memory-clauses">
          {clauses.map((clause, n) => (
            <li key={n}>{clause}</li>
          ))}
        </ul>
      ) : (
        <p key={i}>
          <Inline text={text} />
        </p>
      ),
    );
  }
  return (
    <div className="solar-source-prose" data-testid="brain-content">
      {blocks}
    </div>
  );
}

export function sourceExplanation(entry, content) {
  if (entry.kind === "skill")
    return {
      label: "Skill description",
      text: "The description of a skill installed for your agent. Its full instructions are not included in this view.",
    };
  if (entry.kind === "memory" || entry.kind === "preference")
    return {
      label:
        entry.kind === "memory" ? "Working memory" : "Saved team preference",
      text: "A compact note saved in your agent’s memory. The original conversation is not linked to this note.",
    };
  if (entry.kind === "learning")
    return {
      label: content?.includes("[Excerpt continues in source]")
        ? "Playbook excerpt"
        : "Saved playbook",
      text: content?.includes("[Excerpt continues in source]")
        ? "This is an excerpt of a longer saved reference. The remaining text is not included in this view."
        : "The saved reference text from your agent’s skill library.",
    };
  if (entry.view === "shared")
    return {
      label: "Shared source summary",
      text: "Published guidance and its citation. Your agent can retrieve the guidance through its authorized Brain connection.",
    };
  return {
    label:
      entry.origin === "file"
        ? "Source file"
        : content
          ? "Published knowledge"
          : "Published summary",
    text:
      entry.origin === "file"
        ? "An original file published to your workspace."
        : content
          ? "The source content published to your brand’s workspace."
          : "The published summary is available here; the original text is not included.",
  };
}
